import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useLocation } from "react-router-dom";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import { useTitle } from "utils/UseTitle";
import { cleanInput, initialPageFilterState } from "helpers/methods";
import { AddContactFields, pageFilters } from "models/interfaces";
import { hasAccessTo } from "layouts/Config";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import Unauthorized from "screens/Shared/Unauthorized";
import { useAppSelector } from "hooks/use-app-select";
import { selectScreen } from "redux/appSlice";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import MaintenanceBanner from "global/components/MaintenanceBanner/MaintenanceBanner";
import BulkUploadDialog from "./BulkUpload";
import {
  AddUpdateContactHandler,
  fetchMoreContacts,
  getTableData,
} from "./ContactFunction";
import contactsStyles from "./Contacts.styles";
import contactsTokens, {
  contactsResponsiveTokens,
  getContactTint,
} from "./Contacts.tokens";
import ContactsList from "./components/ContactsList";
import ContactDetailForm, {
  ContactDetailSkeleton,
} from "./components/ContactDetailForm";
import {
  CirclePlusIcon,
  PersonAddIcon,
  UploadIcon,
} from "./components/ContactsIcons";
import EmptyState from "global/components/EmptyState/EmptyState";
import { tourAttr, tourAttrIf } from "tour";

const styles = contactsStyles;
const { secondaryButton, primaryButton, freshState } = contactsTokens;
const { phone, tablet } = contactsResponsiveTokens;

const emptyContactForm: AddContactFields = {
  emailId: "",
  firstName: "",
  lastName: "",
  contactNo: "",
  jobTitle: "",
  companyName: "",
  department: "",
  managerEmail: "",
  address: { street: "", city: "", state: "", zip: "", country: "" },
};

const emptyContactErrors = {
  emailId: "",
  firstName: "",
  lastName: "",
  managerEmail: "",
  contactNo: "",
  jobTitle: "",
  companyName: "",
  department: "",
};

/**
 * Extra pages the list may pull purely to fill a tall pane. Five pages is far
 * more than any viewport needs; past that, something is wrong with the layout
 * rather than with the data.
 */
const MAX_AUTO_FILL_ROUNDS = 5;

/** Per-field character budgets the API enforces. */
const getMaxLength = (name: string) => {
  if (name === "managerEmail") return 255;
  if (name === "jobTitle" || name === "companyName" || name === "department") {
    return 150;
  }
  return 100;
};

const Contacts = () => {
  useTitle(title.CONTACTS);
  const listRef = useRef<HTMLDivElement>(null);
  const autoFillRounds = useRef(0);
  const urlParams = new URLSearchParams(useLocation().search);
  const searchFromUrl = urlParams.get("search") ?? "";

  const isMobile = useAppSelector(selectScreen);
  /**
   * The two small references part company at `sm`: the tablet keeps the
   * master-detail split (one 310px list beside the form) while the phone shows
   * one pane at a time. The redux `isMobile` flag cannot make that call — it is
   * a single `width <= 899` boolean recomputed on resize, so it is true for the
   * tablet as well and stale whenever the viewport starts small.
   */
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  /** Phone *and* tablet — both carry the screen name in the app bar. */
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const [contacts, setContacts] = useState<AddContactFields[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [contactsFilters, setContactsFilters] = useState<pageFilters>(
    initialPageFilterState(searchFromUrl),
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [form, setForm] = useState<AddContactFields>({ ...emptyContactForm });
  const [errors, setErrors] = useState<{ [key: string]: any }>({
    ...emptyContactErrors,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDetailsOnMobile, setShowDetailsOnMobile] = useState(false);
  const [isUploadCsvOpen, setIsUploadCsvOpen] = useState<boolean>(false);
  const [csvUpgradeOpen, setCsvUpgradeOpen] = useState(false);
  const [csvMaintenanceOpen, setCsvMaintenanceOpen] = useState(false);
  const [csvMaintenanceMsg, setCsvMaintenanceMsg] = useState<
    string | undefined
  >();
  /**
   * Set whenever the list is refetched from page 1 (mount, search, save). The
   * effect below then re-seeds the form from the new first row — appending a
   * page must never move the selection, so this cannot key off `contacts`.
   */
  const [shouldResetSelection, setShouldResetSelection] = useState(true);

  const hasContactFetchPermission = hasAccessTo(
    strings.resources.contacts,
    strings.fetchPermission,
  );
  const hasAddPermission = hasAccessTo(
    strings.resources.contacts,
    strings.addPermission,
  );
  const hasUpdatePermission = hasAccessTo(
    strings.resources.contacts,
    strings.updatePermission,
  );
  const canEdit =
    (isCreatingNew && hasAddPermission) ||
    (!isCreatingNew && hasUpdatePermission);

  const csvImportState = useFeatureState(FeatureKeys.CONTACT_CSV_IMPORT);
  const isCsvImportAvailable =
    csvImportState.status === strings.FEATURE_STATUS_AVAILABLE;
  const csvImportTooltip = isCsvImportAvailable
    ? ""
    : csvImportState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((csvImportState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : strings.CONTACT_CSV_UPGRADE_TOOLTIP;

  const hasSearch = Boolean(contactsFilters.searchText);
  /** A brand-new account: nothing stored, nothing searched for. */
  const isFreshAccount =
    !isLoading && !hasSearch && contacts.length === 0 && !isCreatingNew;

  const loadFirstPage = () => {
    if (!hasContactFetchPermission) return;
    setPage(1);
    setHasMore(true);
    setShouldResetSelection(true);
    autoFillRounds.current = 0;
    getTableData(
      1,
      setIsLoading,
      setCount,
      setHasMore,
      setContacts,
      contactsFilters,
    );
  };

  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsFilters]);

  // Re-seed the form from the first row once a page-1 fetch settles.
  useEffect(() => {
    if (!shouldResetSelection || isLoading) return;
    setShouldResetSelection(false);
    setErrors({ ...emptyContactErrors });
    if (isCreatingNew) return;
    if (contacts.length > 0) {
      setSelectedIndex(0);
      setForm({ ...emptyContactForm, ...contacts[0] });
    } else {
      setSelectedIndex(-1);
      setForm({ ...emptyContactForm });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, isLoading, shouldResetSelection]);

  const handleScroll = async () => {
    const element = listRef.current;
    if (!element || isPaginating || isLoading || !hasMore) return;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 50) {
      setIsPaginating(true);
      await fetchMoreContacts(
        page,
        setPage,
        contactsFilters,
        setHasMore,
        setContacts,
      );
      setIsPaginating(false);
    }
  };

  // The list pane is tall enough to show more than one page, so keep pulling
  // until it actually overflows — otherwise scrolling can never be triggered.
  // `autoFillRounds` caps it: if the pane ever loses its bounded height the
  // overflow test can never pass, and without the cap this would page through
  // the entire address book.
  useEffect(() => {
    const element = listRef.current;
    if (!element || isLoading || isPaginating || !hasMore) return;
    if (autoFillRounds.current >= MAX_AUTO_FILL_ROUNDS) return;
    if (element.scrollHeight <= element.clientHeight + 50) {
      autoFillRounds.current += 1;
      (async () => {
        setIsPaginating(true);
        await fetchMoreContacts(
          page,
          setPage,
          contactsFilters,
          setHasMore,
          setContacts,
        );
        setIsPaginating(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, hasMore, isLoading, isPaginating]);

  const handleSearchChange = (event: any) => {
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      searchText: event.target.value,
    });
  };

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setForm({ ...emptyContactForm, ...contacts[index] });
    setIsCreatingNew(false);
    setErrors({ ...emptyContactErrors });
    if (isPhone) setShowDetailsOnMobile(true);
  };

  const handleAddContact = () => {
    setIsCreatingNew(true);
    setForm({ ...emptyContactForm });
    setErrors({ ...emptyContactErrors });
    setSelectedIndex(-1);
    if (isPhone) setShowDetailsOnMobile(true);
  };

  const handleBulkUploadClick = () => {
    if (isCsvImportAvailable) {
      setIsUploadCsvOpen(true);
      return;
    }
    if (csvImportState.status === strings.FEATURE_STATUS_NOT_IN_PLAN) {
      setCsvUpgradeOpen(true);
    } else {
      setCsvMaintenanceMsg((csvImportState as any).disabledMessage);
      setCsvMaintenanceOpen(true);
    }
  };

  const handleChangeField = (event: any) => {
    const { name, value } = event.target;
    const cleaned = typeof value === "string" ? cleanInput(value) : value;
    setForm({ ...form, [name]: cleaned.substring(0, getMaxLength(name)) });
    setErrors({ ...errors, [name]: "" });
  };

  const handleChangeAddressField = (event: any) => {
    const { name, value } = event.target;
    const cleaned = typeof value === "string" ? cleanInput(value) : value;
    setForm({
      ...form,
      address: { ...(form.address ?? {}), [name]: cleaned.substring(0, 100) },
    });
  };

  const handleChangeCountry = (country: string) => {
    setForm({ ...form, address: { ...(form.address ?? {}), country } });
  };

  const handleChangePhone = (phone: string) => {
    setForm({ ...form, contactNo: phone.substring(0, 50) });
    setErrors({ ...errors, contactNo: "" });
  };

  const handleCancel = () => {
    setIsCreatingNew(false);
    setErrors({ ...emptyContactErrors });
    if (contacts.length > 0) {
      const index = selectedIndex >= 0 ? selectedIndex : 0;
      setSelectedIndex(index);
      setForm({ ...emptyContactForm, ...contacts[index] });
    } else {
      setForm({ ...emptyContactForm });
    }
    if (isPhone) setShowDetailsOnMobile(false);
  };

  const handleSave = () => {
    AddUpdateContactHandler(
      setIsSaving,
      setCount,
      setHasMore,
      setContacts,
      contactsFilters,
      form,
      errors,
      setErrors,
      isCreatingNew,
      setIsCreatingNew,
      setSelectedIndex,
      () => {
        setPage(1);
        if (isPhone) setShowDetailsOnMobile(false);
      },
      contacts,
    );
  };

  /**
   * Below `md` both references drop the page `<h1>` and the two header buttons
   * and carry the screen name — plus the one action that still matters — in the
   * app bar. The phone swaps that for the open contact's name and a back arrow
   * while the detail pane is up, exactly as the reference draws it.
   *
   * Both nodes are memoised: `useSetPageHeader` compares them by identity, so a
   * fresh element every render would re-register the header every render.
   */
  const detailHeading = isCreatingNew
    ? "New Contact"
    : `${form.firstName ?? ""} ${form.lastName ?? ""}`.trim();
  const isPhoneDetail = isPhone && showDetailsOnMobile;

  const appBarTitle = useMemo(() => {
    if (!isCompact) return undefined;
    if (isPhone) return isPhoneDetail ? detailHeading : "Contacts";
    return (
      <Box component="span" sx={styles.appBarTitle}>
        Contacts
        {!isLoading && (
          <Box component="span" sx={styles.appBarCount}>
            {count.toLocaleString()} total
          </Box>
        )}
      </Box>
    );
  }, [isCompact, isPhone, isPhoneDetail, detailHeading, isLoading, count]);

  // Held in a ref so the memo below does not have to list every state setter
  // the handler closes over — and so it never fires a stale one.
  const addContactRef = useRef<() => void>(handleAddContact);
  addContactRef.current = handleAddContact;

  const appBarActions = useMemo(() => {
    if (!isCompact || !hasAddPermission || isPhoneDetail) {
      return undefined;
    }
    const onClick = () => addContactRef.current();
    if (isPhone) {
      return (
        <IconButton
          id="contact_add_btn"
          aria-label="Add contact"
          onClick={onClick}
          sx={styles.appBarAddIconButton}
        >
          <CirclePlusIcon
            sx={{
              width: phone.appBarAction.iconSize,
              height: phone.appBarAction.iconSize,
            }}
          />
        </IconButton>
      );
    }
    return (
      <Button
        id="contact_add_btn"
        variant="outlined"
        onClick={onClick}
        sx={styles.appBarAddButton}
      >
        <CirclePlusIcon
          sx={{
            width: tablet.appBarAction.iconSize,
            height: tablet.appBarAction.iconSize,
          }}
        />
        Add
      </Button>
    );
  }, [isCompact, isPhone, isPhoneDetail, hasAddPermission]);

  useSetPageHeader(
    appBarTitle,
    isPhoneDetail ? handleCancel : undefined,
    appBarActions,
  );

  const getHeaderActions = () => {
    if (!hasAddPermission) return null;
    return (
      <Box sx={styles.headerActions}>
        <Button
          id="contact_add_btn"
          variant="outlined"
          disabled={isLoading || isSaving}
          onClick={handleAddContact}
          sx={styles.secondaryButton}
          {...tourAttr("contacts.addBtn")}
        >
          <PersonAddIcon
            strokeWidth={2.2}
            sx={{
              width: secondaryButton.iconSize,
              height: secondaryButton.iconSize,
            }}
          />
          Add Contact
        </Button>
        <Tooltip
          title={csvImportTooltip}
          arrow
          describeChild
          disableHoverListener={isCsvImportAvailable}
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Button
              id="contact_import_btn"
              variant="contained"
              disableElevation
              disabled={isLoading || isSaving || !isCsvImportAvailable}
              onClick={handleBulkUploadClick}
              endIcon={
                !isCsvImportAvailable ? (
                  <LockOutlinedIcon
                    sx={{ fontSize: primaryButton.lockIconSize }}
                  />
                ) : undefined
              }
              sx={styles.primaryButton}
              {...tourAttr("contacts.bulkUpload")}
            >
              <UploadIcon
                strokeWidth={2.2}
                sx={{
                  width: primaryButton.iconSize,
                  height: primaryButton.iconSize,
                }}
              />
              Bulk Upload
            </Button>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  const getHeader = () => {
    // The tablet reference has no page header at all: the app bar carries both
    // the screen name and the "693 total" count.
    if (isCompact && !isPhone) return null;

    // The phone keeps only the count line — and drops even that once the detail
    // pane has taken the screen.
    if (isPhone) {
      if (showDetailsOnMobile) return null;
      return (
        <Box sx={styles.phoneHeader}>
          {isLoading ? (
            <Skeleton
              animation="wave"
              variant="text"
              height={18}
              width={150}
              sx={styles.skeleton}
            />
          ) : (
            <Typography component="p" sx={styles.phoneSubtitle}>
              <Box component="b" sx={styles.headerCount}>
                {count.toLocaleString()}
              </Box>{" "}
              total contacts
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box sx={styles.header}>
        <Box>
          <Typography component="h1" sx={styles.headerTitle}>
            Contacts
          </Typography>
          {isLoading ? (
            <Skeleton
              animation="wave"
              variant="text"
              height={22}
              width={160}
              sx={styles.skeletonSubtitle}
            />
          ) : (
            <Typography component="p" sx={styles.headerSubtitle}>
              <Box component="b" sx={styles.headerCount}>
                {count.toLocaleString()}
              </Box>{" "}
              total contacts
            </Typography>
          )}
        </Box>
        {getHeaderActions()}
      </Box>
    );
  };

  const getFreshState = () => (
    <Box sx={styles.freshWrap} {...tourAttr("contacts.list")}>
      <EmptyState
        framed
        icon="contact"
        accent="violet"
        title="No contacts yet"
        body="Contacts are saved automatically when you send sign requests — or add them yourself to speed up recipient selection later."
        primaryAction={
          hasAddPermission
            ? { label: "Add your first contact", onClick: handleAddContact }
            : undefined
        }
        secondaryAction={
          isCsvImportAvailable
            ? { label: "Bulk upload CSV", onClick: handleBulkUploadClick }
            : undefined
        }
      />
    </Box>
  );

  const getList = () => (
    <ContactsList
      contacts={contacts}
      selectedIndex={selectedIndex}
      onSelect={handleSelect}
      initialSearchText={searchFromUrl}
      searchText={contactsFilters.searchText ?? ""}
      onSearchChange={handleSearchChange}
      isLoading={isLoading}
      isPaginating={isPaginating}
      hasSearch={hasSearch}
      scrollRef={listRef}
      onScroll={handleScroll}
    />
  );

  const getDetail = () => {
    if (isLoading || isSaving) return <ContactDetailSkeleton />;
    return (
      <ContactDetailForm
        form={form}
        errors={errors}
        tint={getContactTint(selectedIndex < 0 ? 0 : selectedIndex)}
        isCreatingNew={isCreatingNew}
        canEdit={canEdit}
        canEditEmail={hasAddPermission && isCreatingNew}
        isSaving={isSaving}
        onChangeField={handleChangeField}
        onChangeAddressField={handleChangeAddressField}
        onChangeCountry={handleChangeCountry}
        onChangePhone={handleChangePhone}
        onSave={handleSave}
        onCancel={handleCancel}
        // The phone reference puts the back arrow in the app bar next to the
        // contact's name (see `useSetPageHeader` above), so the form itself
        // never needs one of its own.
        showBackButton={false}
        onBack={() => setShowDetailsOnMobile(false)}
      />
    );
  };

  const getCard = () => {
    if (isFreshAccount) return getFreshState();
    if (isPhone) {
      return (
        <Box sx={styles.card}>
          {showDetailsOnMobile ? getDetail() : getList()}
        </Box>
      );
    }
    return (
      <Box sx={styles.card}>
        {getList()}
        {getDetail()}
      </Box>
    );
  };

  if (!hasContactFetchPermission) {
    return <Unauthorized permissionDenied={hasContactFetchPermission} />;
  }

  // Contacts sits on the shared centred column, the same wrapper Templates and
  // Sign Requests use; the screen adds only its own inner padding.
  return (
    <CenteredAuthenticatedLayout>
      <Box sx={styles.page} {...tourAttrIf(!isLoading, "contacts.ready")}>
        {getHeader()}
        {getCard()}
      </Box>
      <BulkUploadDialog
        isUploadCsvOpen={isUploadCsvOpen}
        setIsUploadCsvOpen={setIsUploadCsvOpen}
        setIsLoading={setIsLoading}
        setCount={setCount}
        setHasMore={setHasMore}
        setContacts={setContacts}
        contactsFilters={contactsFilters}
        onlyUpload={!hasAddPermission && hasUpdatePermission}
        isMobile={isMobile}
      />
      <UpgradeModal
        open={csvUpgradeOpen}
        featureKey={FeatureKeys.CONTACT_CSV_IMPORT}
        onClose={() => setCsvUpgradeOpen(false)}
      />
      <MaintenanceBanner
        open={csvMaintenanceOpen}
        message={csvMaintenanceMsg}
        onClose={() => setCsvMaintenanceOpen(false)}
      />
    </CenteredAuthenticatedLayout>
  );
};

export default Contacts;
